import { useMemo } from 'react';
import { Box, Card } from '@nimbus-ds/components';
import { PaymentFeeDto, TaxDto } from '@tiendanube/common';
import { CheckoutPaymentMethod } from '@tiendanube/common/src/enums';
import {
  useChargesToPay,
  usePayorderToPay,
} from 'domains/Billing/Checkout/hooks';
import {
  AccountDataContent,
  ChargesSelectionSummary,
  PaymentDataContent,
} from 'domains/Billing/Checkout/pages/components';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import { ChargeDisplayInterface } from '../PaymentDataContent/PaymentDataContent';

interface PayOrderSummaryProps {
  showTaxes?: boolean;
  paymentMethod?: CheckoutPaymentMethod;
}

function transformExtraFeesToChargeDisplay(
  description: string,
  fees?: PaymentFeeDto[],
): [ChargeDisplayInterface[], TaxDto[]] {
  if (!fees) return [[], []];
  const charges: ChargeDisplayInterface[] = [];
  let taxes: TaxDto[] = [];

  fees.forEach((fee) => {
    taxes = taxes.concat(fee.taxes);
    charges.push({
      amountValue: fee.amountValue,
      amountCurrency: fee.amountCurrency,
      conceptCode: fee.concept.code,
      description,
    });
  });

  return [charges, taxes];
}

function PayOrderSummary({
  showTaxes = false,
  paymentMethod,
}: PayOrderSummaryProps) {
  const { transformChargesToDisplayable } = useChargesToPay(false);
  const {
    payorderToPay,
    isSuccess,
    totalWithoutFees,
    subtotalWithoutFees,
    totalFeeForMethod,
    nonFeeCharges,
    feesForMethod,
  } = usePayorderToPay();
  const t = useTranslationBilling('checkout.payOrderSummary');

  const feeTotal = (paymentMethod && totalFeeForMethod(paymentMethod)) ?? 0;

  const charges = useMemo(
    () => nonFeeCharges && transformChargesToDisplayable(nonFeeCharges),
    [nonFeeCharges, transformChargesToDisplayable],
  );

  if (!isSuccess || !payorderToPay || !totalWithoutFees || !subtotalWithoutFees)
    return null;

  const [extraFeeCharges, extraFeeTaxes] = transformExtraFeesToChargeDisplay(
    t('extraFeeDescription'),
    paymentMethod && feesForMethod(paymentMethod),
  );

  return (
    <Card padding="none">
      <Card.Header padding="base" title={t('total')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" flexGrow="1">
          <Box padding="4">
            <AccountDataContent />
          </Box>
          <hr />
          <Box padding="4">
            {charges && (
              <PaymentDataContent charges={[...charges, ...extraFeeCharges]} />
            )}
          </Box>
          <hr />
          <Box padding="4" backgroundColor="neutral-surface">
            {charges && (
              <ChargesSelectionSummary
                charges={charges}
                showTaxes={showTaxes}
                showDiscounts
                extraFee={feeTotal}
                extraTaxes={extraFeeTaxes}
              />
            )}
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

export default PayOrderSummary;
